/**
 * Client-side opt-in for the shared Postgres sample (isDemo) datasource.
 *
 * The API always returns the shared Postgres demo. Signed-in users hide it by
 * default so the first-run screen stays focused on connecting their own sheet.
 * Guests who choose "Open the demo as a guest" turn it on. Direct demo URLs
 * still work even when the preference is off.
 *
 * MOSA-908. A Google Sheets sample is different: the API returns it only for
 * the account that chose "Try a sample sheet". When it is in the list, show
 * it. Do not hide it behind this preference.
 *
 * The storage key includes the account id so a guest opt-in does not follow a
 * later signed-in user (and the reverse).
 */

const STORAGE_PREFIX = "od:show-sample-demo";

export type DemoLike = {
  id?: string;
  isDemo?: boolean;
  readOnly?: boolean;
  /** Datasource kind. Sheets samples are per-account; Postgres is shared. */
  type?: string;
};

/** Only the shared sample. Invited / read-only project sources stay visible. */
export function isDemoDatasource(datasource: DemoLike | null | undefined): boolean {
  return !!datasource?.isDemo;
}

/**
 * MOSA-908. A Sheets sample the caller chose. The API lists it only for that
 * account, so the client does not filter it with the Postgres opt-in flag.
 */
export function isUserSampleSheet(
  datasource: DemoLike | null | undefined,
): boolean {
  return (
    !!datasource?.isDemo && String(datasource.type ?? "") === "GOOGLE_SHEETS"
  );
}

/** Shared Postgres demo that needs the local opt-in flag. */
export function isSharedDemoDatasource(
  datasource: DemoLike | null | undefined,
): boolean {
  return isDemoDatasource(datasource) && !isUserSampleSheet(datasource);
}

export function sampleStorageKey(accountId: string | null | undefined): string {
  const id = (accountId ?? "").trim() || "anonymous";
  return `${STORAGE_PREFIX}:${id}`;
}

export function isSampleVisible(accountId?: string | null): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(sampleStorageKey(accountId)) === "1";
  } catch {
    return false;
  }
}

export function setSampleVisible(
  visible: boolean,
  accountId?: string | null,
): void {
  if (typeof window === "undefined") return;
  try {
    const key = sampleStorageKey(accountId);
    if (visible) {
      window.localStorage.setItem(key, "1");
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Private mode: keep the in-memory session only.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("od:sample-visible-change"));
  }
}

export function showSample(accountId?: string | null): void {
  setSampleVisible(true, accountId);
}

export function hideSample(accountId?: string | null): void {
  setSampleVisible(false, accountId);
}

/**
 * Sources that count for the sidebar and first-run. The shared Postgres demo
 * does not count. A Sheets sample the user chose does count.
 */
export function ownDatasources<T extends DemoLike>(datasources: T[] | undefined): T[] {
  return (datasources ?? []).filter(
    (datasource) =>
      !isDemoDatasource(datasource) || isUserSampleSheet(datasource),
  );
}

/**
 * Sources to list in the sidebar and on Data Sources.
 * The shared Postgres sample appears only when the user opted in, or when they
 * are already looking at it. A Sheets sample always appears when the API
 * returned it (the user chose it).
 */
export function visibleDatasources<T extends DemoLike>(
  datasources: T[] | undefined,
  options: { showSample: boolean; viewingDatasourceId?: string | null } = {
    showSample: false,
  },
): T[] {
  const list = datasources ?? [];
  const viewingId = options.viewingDatasourceId ?? null;
  return list.filter((datasource) => {
    if (!isDemoDatasource(datasource)) return true;
    if (isUserSampleSheet(datasource)) return true;
    if (options.showSample) return true;
    return !!viewingId && datasource.id === viewingId;
  });
}
